import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import { FiX } from "react-icons/fi";
import useGoogleMapsLoader from "../../../../../Hooks/useGoogleMapsLoader";

const mapContainerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

const parseCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isNaN(number) ? null : number;
};

const toRadians = (value) => (value * Math.PI) / 180;

const getDistanceInMeters = (from, to) => {
  if (!from || !to) return Number.POSITIVE_INFINITY;
  const earthRadius = 6371000;
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

async function reverseGeocode(lat, lng) {
  const googleMaps = window.google?.maps;
  if (googleMaps?.Geocoder) {
    const geocoder = new googleMaps.Geocoder();
    const result = await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]?.formatted_address ?? "");
        } else {
          resolve("");
        }
      });
    });
    if (result) return result;
  }

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`,
      { headers: { Accept: "application/json" } }
    );
    const json = await res.json();
    return json?.display_name ?? "";
  } catch {
    return "";
  }
};

const DrainSlitComplaintMapView = ({
  latitude,
  longitude,
  initialAddress = "",
  height = "320px",
  onLocationSelect,
}) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const userPickedLocationRef = useRef(false);
  const watchIdRef = useRef(null);
  const geoTimeoutRef = useRef(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  const resolvedAddressRef = useRef("");
  const lastGeocodedRef = useRef(null);
  const currentPositionRef = useRef(null);
  const geoInitializedRef = useRef(false);
  const lastPannedRef = useRef(null);

  const [showInfo, setShowInfo] = useState(true);
  const [resolvedAddress, setResolvedAddress] = useState(initialAddress || "");
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [detectedPosition, setDetectedPosition] = useState(null);

  const propPosition = useMemo(() => {
    const lat = parseCoordinate(latitude);
    const lng = parseCoordinate(longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [latitude, longitude]);

  const position = detectedPosition || propPosition || null;

  const mapCenter = useMemo(
    () => position || { lat: 0, lng: 0 },
    [position?.lat, position?.lng]
  );

  const infoWindowOptions = useMemo(() => {
    if (!window.google?.maps) return { headerDisabled: true };
    return {
      pixelOffset: new window.google.maps.Size(0, -36),
      headerDisabled: true,
    };
  }, [isLoaded, position?.lat, position?.lng]);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    resolvedAddressRef.current = resolvedAddress || initialAddress || "";
  }, [resolvedAddress, initialAddress]);

  useEffect(() => {
    if (!isLoaded || !position) {
      return undefined;
    }

    const lastGeocoded = lastGeocodedRef.current;
    if (
      lastGeocoded &&
      getDistanceInMeters(lastGeocoded, position) < 8 &&
      resolvedAddressRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    setIsResolvingAddress(true);

    const resolveAddress = async () => {
      const addr = await reverseGeocode(position.lat, position.lng);
      if (cancelled) return;

      if (addr) {
        setResolvedAddress(addr);
        resolvedAddressRef.current = addr;
        lastGeocodedRef.current = position;

        if (onLocationSelectRef.current) {
          onLocationSelectRef.current(
            position.lat.toFixed(6),
            position.lng.toFixed(6),
            addr,
            false
          );
        }
      }

      setIsResolvingAddress(false);
    };

    resolveAddress();

    return () => {
      cancelled = true;
      setIsResolvingAddress(false);
    };
  }, [isLoaded, position?.lat, position?.lng]);

  useEffect(() => {
    currentPositionRef.current = detectedPosition || propPosition || null;
  }, [detectedPosition, propPosition]);

  const applyLocation = useCallback(
    async (lat, lng, { updateForm = true, forceAddress = false } = {}) => {
      const lastGeocoded = lastGeocodedRef.current;
      if (
        lastGeocoded &&
        getDistanceInMeters(lastGeocoded, { lat, lng }) < 8 &&
        resolvedAddressRef.current
      ) {
        if (updateForm && onLocationSelectRef.current) {
          onLocationSelectRef.current(
            lat.toFixed(6),
            lng.toFixed(6),
            forceAddress ? resolvedAddressRef.current : resolvedAddressRef.current,
            forceAddress
          );
        }
        return resolvedAddressRef.current;
      }

      const addr = await reverseGeocode(lat, lng);
      setResolvedAddress(addr);
      resolvedAddressRef.current = addr;
      lastGeocodedRef.current = { lat, lng };

      if (updateForm && onLocationSelectRef.current && (forceAddress || addr)) {
        onLocationSelectRef.current(lat.toFixed(6), lng.toFixed(6), addr, forceAddress);
      }

      return addr;
    },
    []
  );

  useEffect(() => {
    if (!isLoaded || userPickedLocationRef.current || geoInitializedRef.current) {
      return undefined;
    }

    geoInitializedRef.current = true;

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported in this browser.");
      return undefined;
    }

    setLocationLoading(true);
    setLocationError("");

    let bestAccuracy = Number.POSITIVE_INFINITY;

    const onPosition = async (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const accuracy = Number(pos.coords.accuracy ?? Number.POSITIVE_INFINITY);
      const nextPosition = { lat, lng };
      const currentPosition = currentPositionRef.current;
      const movedDistance = getDistanceInMeters(currentPosition, nextPosition);
      const isSignificantMove = movedDistance > 12;
      const isMeaningfulAccuracyGain = bestAccuracy - accuracy > 10;

      if (
        accuracy <= bestAccuracy &&
        (isSignificantMove || isMeaningfulAccuracyGain || !currentPosition)
      ) {
        bestAccuracy = accuracy;
        setDetectedPosition(nextPosition);
        setLocationLoading(false);

        if (!userPickedLocationRef.current) {
          await applyLocation(lat, lng, { forceAddress: true });
        }
      }
    };

    const onError = () => {
      setLocationLoading(false);
      setLocationError("Unable to detect current location.");
    };

    navigator.geolocation.getCurrentPosition(onPosition, onError, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });

    watchIdRef.current = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0,
    });

    geoTimeoutRef.current = setTimeout(() => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    }, 15000);

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (geoTimeoutRef.current) {
        clearTimeout(geoTimeoutRef.current);
        geoTimeoutRef.current = null;
      }
    };
  }, [isLoaded, applyLocation]);

  useEffect(() => {
    if (!mapRef.current || !position) return;

    const lastPanned = lastPannedRef.current;
    if (
      lastPanned &&
      getDistanceInMeters(lastPanned, position) < 1
    ) {
      return;
    }

    lastPannedRef.current = position;
    mapRef.current.panTo(position);
  }, [position?.lat, position?.lng]);

  const handleMapClick = async (event) => {
    userPickedLocationRef.current = true;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setDetectedPosition({ lat, lng });
    await applyLocation(lat, lng, { forceAddress: true });
    setShowInfo(true);
  };

  const displayAddress =
    resolvedAddress ||
    initialAddress ||
    (isResolvingAddress ? "Resolving address..." : "N/A");

  if (loadError) {
    return <div className="text-danger p-3">Unable to load map.</div>;
  }

  return (
    <div
      style={{
        height,
        width: "100%",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #E0E0E0",
        background: "#F8FAFC",
      }}
    >
      {!isLoaded || locationLoading ? (
        <div className="d-flex align-items-center justify-content-center text-muted h-100">
          {locationLoading ? "Detecting current location..." : "Loading map..."}
        </div>
      ) : !position ? (
        <div className="d-flex align-items-center justify-content-center text-muted h-100">
          {locationError || "Current location is required to load the map."}
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={18}
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={(map) => {
            mapRef.current = map;
            map.panTo(position);
          }}
        >
          <Marker position={position} onClick={() => setShowInfo(true)} />
          {showInfo && window.google?.maps && (
            <InfoWindow
              position={position}
              onCloseClick={() => setShowInfo(false)}
              options={infoWindowOptions}
            >
              <div style={{ minWidth: 220, maxWidth: 320, paddingRight: 20, position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setShowInfo(false)}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    border: "none",
                    background: "transparent",
                    color: "#64748b",
                    cursor: "pointer",
                  }}
                >
                  <FiX size={15} />
                </button>
                <div style={{ display: "grid", gap: 8, fontSize: 12, color: "#1e293b" }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#64748b" }}>Address :</div>
                    <div style={{ fontWeight: 600 }}>{displayAddress}</div>
                  </div>
                  <div>Latitude : {position.lat.toFixed(6)}</div>
                  <div>Longitude : {position.lng.toFixed(6)}</div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) =>
  prevProps.latitude === nextProps.latitude &&
  prevProps.longitude === nextProps.longitude &&
  prevProps.height === nextProps.height;

export default memo(DrainSlitComplaintMapView, arePropsEqual);
